import { pick } from 'lodash'
import moment from 'moment'
import {
  campaignPremiumTypes,
  premiumTypes,
  subscriptions,
  subscriptionTypes,
  registrationRoutines,
  goalType
} from '@/views/partners/fidelityCampaign/fidelityCampaignModels'
import main from '@/assets/theme/sdz/main'
import IMask from 'imask'

export const FidelityCampaignMapper = (generalForm, partnerForm, premiumForm) => {
  return {
    ...generalMapper(generalForm),
    ...partnerMapper(partnerForm),
    ...premiumMapper(premiumForm)
  }

  function generalMapper(form) {
    const sanitizedData = {
      campaign: form.name,
      initial: moment(form.initial).format('DD/MM/YYYY'),
      end: moment(form.end).format('DD/MM/YYYY')
    }

    return {
      ...pick(form, [
        'name',
        'description',
        'tasks',
        'platforms',
        'reminder',
        'goalType',
        'goal'
      ]),
      ...sanitizedData
    }
  }

  function partnerMapper(form) {
    const mappedCompanies = {
      selectedCompanies: form.selectedCompanies.map(company => company._id)
    }

    return {
      ...pick(form, [
        'subscription',
        'subscriptionType',
        'participantSegmentation',
        'registrationRoutine',
        'isAllCompanies',
        'partner',
        'fileUploadedId'
      ]),
      ...mappedCompanies
    }
  }

  function premiumMapper(form) {
    // switch (type) {
    //   case 'storesOnline':
    //     return storesOnlineMapper()
    //   case 'voucherProducts':
    //     return productsMapper()
    //   case 'coin':
    //     return coinMapper()
    //   case 'voucher':
    //     return voucherMapper()
    // }

    return form.type === 'coin' ? coinMapper() : productsMapper()

    function coinMapper() {
      return {
        balanceProvisioning: form.provideBalance ? form.balanceProvisioning : null,
        ...pick(form, [
          'premiumType',
          'premiumRewardInfo',
          'usersLimit',
          'maxUsersPremiums',
          'provideBalance',
          'type'
        ])
      }
    }

    function productsMapper() {
      return {
        ...pick(form, [
          'premiumType',
          'premiumRewardInfo',
          'usersLimit',
          'maxUsersPremiums',
          'type'
        ])
      }
    }

    // function storesOnlineMapper() {
    //   return {
    //     ...pick(form, ['premiumType', 'premiumValue', 'usersLimit', 'maxUsersPremiums']),
    //   }
    // }

    // function voucherMapper() {
    //   const common = { ...pick(form, ['discountType', 'products']) }

    //   if (form.discountType === 'progressive') {
    //     return {
    //       ...common,
    //       ...pick(form, ['progressiveDiscount'])
    //     }
    //   }

    //   return {
    //     ...common,
    //     ...pick(form, ['discountValueType', 'premiumValue'])
    //   }
    // }
  }
}

/**
 * @param {Object} data
 * @param {Boolean} isResumeFromCreateForm => checks to see what payload should be displayed in Resume,
 * either the payload from CreateForm or the payload from the API
 */

export const ResumeMapper = (data, isResumeFromCreateForm = false) => {
  return {
    type: data.type,
    general: generalMapper(),
    partner: partnerMapper(),
    premium: premiumMapper()
  }

  function generalMapper() {
    const sanitizedData = {}

    if (data.platforms)
      sanitizedData.platforms = data.platforms.map(p => main[p]).join(', ')
    if (data.reminder) sanitizedData.reminder = data.reminder
    if (data.date) {
      sanitizedData.period = data.date
    } else {
      sanitizedData.period = [
        moment(data.initial, 'DD/MM/YYYY').format('DD/MM/YYYY'),
        moment(data.end, 'DD/MM/YYYY').format('DD/MM/YYYY')
      ].join(' até ')
    }
    if (data.statusDescription) sanitizedData.statusDescription = data.statusDescription
    if (data.rules) {
      if (typeof data.rules === 'string') {
        sanitizedData.rules = data.rules
      } else {
        sanitizedData.rules = window.URL.createObjectURL(data.rules, 'application/pdf')
      }
    }

    return {
      ...sanitizedData,
      name: data.name,
      description: data.description,
      tasks: data.tasks,
      goalType: goalType[data.goalType],
      goal: currencyMask(data.goal)
    }
  }

  function partnerMapper() {
    const partnerForm = {
      subscriptionType: subscriptionTypes[data.subscriptionType],
      subscription: subscriptions[data.subscription],
      // participantSegmentation: data.currentParticipantSegmentation?.name,
      // registrationRoutine: registrationRoutines[data.registrationRoutine]
      isAllCompanies: data.isAllCompanies ? 'Sim' : 'Não',
      partnerName: data.partnerName || data.partner?.name
    }

    return data.isAllCompanies
      ? partnerForm
      : {
          ...partnerForm,
          selectedCompanies: data.selectedCompanies
            ?.map(company => company.name)
            .sort(Intl.Collator().compare)
        }
  }

  function premiumMapper() {
    return data.type === 'coin' ? coinMapper() : voucherProductsMapper()

    function coinMapper() {
      const sanitizedData = {}

      if (data.premiumType === 'unique') {
        sanitizedData.premiumInfo = `SDZ ${integerMask(data.premiumRewardInfo.coin)}`
      }

      if (data.premiumType === 'proportional') {
        sanitizedData.premiumInfo = `SDZ ${integerMask(
          data.premiumRewardInfo.coin
        )} a cada ${currencyMask(data.premiumRewardInfo.money)}`
      }

      if (data.provideBalance)
        sanitizedData.balanceProvisioning = integerMask(data.balanceProvisioning)

      return {
        ...sanitizedData,
        type: campaignPremiumTypes[data.type],
        premiumType: data.premiumType,
        usersLimit: data.usersLimit,
        maxUsersPremiums: integerMask(data.maxUsersPremiums),
        provideBalance: data.provideBalance ? 'Sim' : 'Não'
      }
    }

    function voucherProductsMapper() {
      const { products } = data.premiumRewardInfo

      const getPremiumInfo = () => {
        if (!products.length) return 'Nenhum produto válido selecionado para premiação'

        const productNameMapped = isResumeFromCreateForm
          ? products[0]?.productName
          : products[0]?.product?.label

        return `Produto: ${productNameMapped} - Quantidade: ${integerMask(
          data.premiumRewardInfo.products[0].quantity
        )}`
      }

      return {
        premiumInfo: getPremiumInfo(),
        type: 'Produtos',
        usersLimit: integerMask(data.usersLimit),
        maxUsersPremiums: integerMask(data.maxUsersPremiums)
      }
    }

    // function storesOnlineMapper() {
    //   return {
    //     premiumRewardInfo: data.premiumRewardInfo,
    //     premiumType: data.premiumType,
    //     usersLimit: integerMask(data.usersLimit),
    //     maxUsersPremiums: integerMask(data.maxUsersPremiums),
    //   }
    // }

    // function voucherMapper() {
    //   const resultData = data.discountType === 'fix' ? fixMapper() : progressiveMapper();

    //   return {
    //     ...resultData,
    //     products: data.products.map(p => data.productList.find(productWithLabel => productWithLabel._id === p).label).join(', '),
    //     discountType: data.discountType
    //   }

    //   function fixMapper() {
    //     const sanitizedData = {}

    //     if  (data.discountValueType === 'seedz') {
    //       sanitizedData.premiumValue = `SDZ ${integerMask(data.premiumValue)}`;
    //     } else {
    //       sanitizedData.premiumValue = currencyMask(data.premiumValue, '% ');
    //     }

    //     return {
    //       ...sanitizedData,
    //       discountValueType: data.discountValueType
    //     }
    //   }

    //   function progressiveMapper() {
    //     return {
    //       progressiveDiscount: data.progressiveDiscount
    //     }
    //   }
    // }
  }
}

export function currencyMask(value, prefix = 'R$ ') {
  return IMask.pipe(`${value}`, {
    mask: `${prefix}value`,
    blocks: {
      value: {
        mask: Number,
        scale: 2,
        normalizeZeros: true,
        thousandsSeparator: '.',
        padFractionalZeros: true
      }
    }
  })
}

export function integerMask(value) {
  return IMask.pipe(`${value}`, {
    mask: Number,
    scale: 0,
    signed: false,
    thousandsSeparator: '.'
  })
}
